import React, { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  GridLegacy as Grid,
  Typography,
} from '@mui/material';

import ApplyToColumn from './ApplyToColumn';
import type { InclusionApplyTo } from './types';

type ApplyToDimension = 'roomTypes' | 'occupancies' | 'los';

interface Props {
  open: boolean;
  value: InclusionApplyTo;
  roomTypes: Array<string>;
  occupancies: Array<string>;
  nights: Array<number>;
  onClose: () => void;
  onApply: (value: InclusionApplyTo) => void;
}

export default function ApplyInclusionDialog({ open, value, roomTypes, occupancies, nights, onClose, onApply }: Props) {
  const [draft, setDraft] = useState<InclusionApplyTo>(value);

  useEffect(() => {
    if (open) {
      setDraft(value);
    }
  }, [open, value]);

  const patch = (dimension: ApplyToDimension, next: InclusionApplyTo[typeof dimension]) =>
    setDraft((current) => ({ ...current, [dimension]: next }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Apply inclusion to packages</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each column is optional. Leave a column on <strong>Select all</strong> to ignore that filter. Example: only
          Room type = that package for every occupancy and LOS; only Occupancy = 2 = every package with occupancy 2;
          only LOS = 8N = every package at 8 nights.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <ApplyToColumn
              title="Room type"
              items={roomTypes}
              value={draft.roomTypes}
              formatItem={(item) => item}
              onChange={(next) => patch('roomTypes', next)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ApplyToColumn
              title="Occupancy"
              items={occupancies}
              value={draft.occupancies}
              formatItem={(item) => item}
              onChange={(next) => patch('occupancies', next)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <ApplyToColumn
              title="Length of stay"
              items={nights}
              value={draft.los}
              formatItem={(item) => `${item}N`}
              onChange={(next) => patch('los', next)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button
          onClick={() => {
            onApply(draft);
            onClose();
          }}
          variant="contained"
          color="primary"
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
